import styles from "./ClickAndCollectCountdown.module.scss";
import { useEffect, useRef, useState } from "react";

type Props = {
  /**
   * A string representing the order by local time today
   */
  orderByLocalTimeToday: string;
  /**
   * A string representing the collect from local time tomorrow
   */
  collectFromLocalTimeTomorrow: string;
  /**
   * A number representing how many hours prior to the cutoff time that the countdown starts to be displayed.
   */
  countdownDurationHours: number;
  /**
   * A number representing how many minutes prior to the cutoff time that the countdown is hidden.
   */
  hideIfWithinLastMinutes: number;
  /**
   * A function used to record a managed impression in Monetate.
   */
  recordImpression: (value: string) => void;
  /**
   * A boolean that indicates if the countdown should be hidden.
   */
  showClickAndCollectCountdown: boolean;
};

const parseTime = (time: string): { hours: number; minutes: number } => {
  const [h, m] = time.split(":").map((t) => parseInt(t, 10));
  return { hours: h || 0, minutes: m || 0 };
};

const convertToAMPM = (time: string): string | null => {
  const { hours, minutes } = parseTime(time);
  const hour12 = hours % 12 || 12;
  const suffix = hours >= 12 ? "pm" : "am";
  return minutes === 0
    ? `${hour12}${suffix}`
    : `${hour12}:${minutes.toString().padStart(2, "0")}${suffix}`;
};

export const ClickAndCollectCountdownUI = ({
  collectFromLocalTimeTomorrow,
  orderByLocalTimeToday,
  countdownDurationHours,
  hideIfWithinLastMinutes,
  recordImpression,
  showClickAndCollectCountdown,
}: Props) => {
  const { hours, minutes } = parseTime(orderByLocalTimeToday);

  const cutoffTime = new Date();
  cutoffTime.setHours(hours, minutes, 0, 0);

  const [now, setNow] = useState<Date | null>(null);
  const initialLoadTimeRef = useRef(new Date());

  useEffect(() => {
    setNow(new Date());
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  if (!now) return null;

  const timeLeftMs = cutoffTime.getTime() - now.getTime();
  const durationMs = countdownDurationHours * 60 * 60 * 1000;

  const cutoffDelayMs = (hideIfWithinLastMinutes ?? 5) * 60 * 1000;
  const userLoadedTooLate =
    cutoffTime.getTime() - initialLoadTimeRef.current.getTime() < cutoffDelayMs;

  const showTimer = timeLeftMs > 0 && timeLeftMs <= durationMs;

  if (!showTimer || userLoadedTooLate) return null;

  recordImpression("true");

  if (!showClickAndCollectCountdown) return null;

  const h = Math.floor((timeLeftMs / (1000 * 60 * 60)) % 24);
  const m = Math.floor((timeLeftMs / (1000 * 60)) % 60);
  const s = Math.floor((timeLeftMs / 1000) % 60);

  const pluralise = (n: number, word: string) =>
    `${n} ${word}${n !== 1 ? "s" : ""}`;

  const parts = [];
  if (h) parts.push(pluralise(h, "hour"));
  if (m) parts.push(pluralise(m, "minute"));
  if (!h && m < 2 && s) parts.push(pluralise(s, "second"));

  const timeDisplay = parts.join(" ");

  return (
    <div
      className={styles.container}
      data-testid="countdown-message"
      data-component="ClickAndCollectCountdown"
    >
      Order in the next <span className={styles.time}>{timeDisplay}</span> to
      collect in-store from {convertToAMPM(collectFromLocalTimeTomorrow)}{" "}
      tomorrow
    </div>
  );
};
